import { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { client } from '../../../../@api-client/client.gen';
import { ForbiddenContext } from '../forbidden-context';
import { setApplicationContextHeader } from './application-context-header';
import { resolveLoggingData, useCachedAppVersionData } from './use-cached-app-version-data';
import { ApplicationLoggingAppName } from '@idu-nightlife/nightlife-utils';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  },
});

type ApiProviderProps = PropsWithChildren<{
  baseUrl: string;
  appName: ApplicationLoggingAppName;
}>;

export const ApiProviderUnauthenticated = ({
  children,
  baseUrl = '',
  appName,
}: ApiProviderProps) => {
  const [isForbidden, setIsForbidden] = useState(false);
  const forbiddenContextValue = useMemo(() => ({ isForbidden, setIsForbidden }), [isForbidden]);
  const getCachedAppVersionData = useCachedAppVersionData();

  useEffect(() => {
    client.setConfig({
      baseUrl: baseUrl,
    });
  }, [baseUrl]);

  useEffect(() => {
    const requestInterceptorId = client.interceptors.request.use(async (request) => {
      setApplicationContextHeader(request, appName);

      const appVersionData = await getCachedAppVersionData();
      request.headers.set(
        'x-logging-data',
        JSON.stringify(resolveLoggingData({ appVersionData, appName })),
      );

      return request;
    });

    return () => {
      client.interceptors.request.eject(requestInterceptorId);
    };
  }, [appName, getCachedAppVersionData]);

  return (
    <QueryClientProvider client={queryClient}>
      <ForbiddenContext.Provider value={forbiddenContextValue}>
        {children}
      </ForbiddenContext.Provider>
    </QueryClientProvider>
  );
};
